/**
 * Weight Freeze (v2) — Packing's pure model.
 *
 * A freeze is agreed against packing material, not against a SKU: a courier
 * bills on chargeable weight, the greater of dead weight (the product) and
 * volumetric weight (the box). Ship the same SKU in a different carton and
 * the freeze no longer covers that shipment. This module is the arithmetic
 * and pure derivations behind that rule — no React, no mutation. Mutations
 * go through `useFreezeStore.js` in a later task.
 *
 * `round2`/`round3`/`withinTolerance` and the max(dead, vol) invariant are
 * weightMath's own — Section 0 extracted them precisely so every section
 * computing chargeable weight agrees. They are re-exported/aliased here
 * unchanged, never redefined; do not add a second round2/round3/max(dead,vol)
 * to this file.
 */
import {
  round2, round3, withinTolerance, volumetricKg as sharedVolumetricKg, chargeableKg,
} from '../../weightMath'

/** Category › sub-category. A real U+203A, matching the mockup's own arrow glyph. */
export const ARROW = ' › '

/** Stored values, not labels. Never compare against a label. */
export const PACK_KIND = {FLYER: 'flyer', CARTON: 'carton', OTHER: 'other'}
export const PACK_BRANDING = {SELF: 'self', GENERIC: 'generic'}

export const PACK_KIND_LABEL = {
  [PACK_KIND.FLYER]: 'Flyer',
  [PACK_KIND.CARTON]: 'Cardboard box or carton',
  [PACK_KIND.OTHER]: 'Other material',
}
export const PACK_BRANDING_LABEL = {
  [PACK_BRANDING.SELF]: 'Self-branded',
  [PACK_BRANDING.GENERIC]: 'Generic (non-branded)',
}

/**
 * A packing material's branding as text, or null when the record does not say.
 *
 * The product master has no branding or kind column, so a live pack carries null for both
 * (mapPack refuses to invent them). Rendering it straight left the picker showing
 * "channel order test - Carton ·" — a separator with nothing after it, on every option.
 */
export function brandingLabel(branding) {
  if (branding == null || branding === '') return null
  return PACK_BRANDING_LABEL[branding] || branding
}

/** "Flyer L · Unbranded", or just "Flyer L" when branding was never recorded. */
export function packOptionLabel(pack) {
  if (!pack) return ''
  const brand = brandingLabel(pack.branding)
  return brand ? `${pack.label} · ${brand}` : pack.label
}

/** Id stems for a newly minted material, keyed by kind. */
const PACK_ID_STEM = {[PACK_KIND.CARTON]: 'BOX', [PACK_KIND.FLYER]: 'FLY', [PACK_KIND.OTHER]: 'MAT'}

export {round2, round3, withinTolerance}

/** The single definition of chargeable weight, re-exported under this module's own name. */
export const chargeable = chargeableKg

/**
 * l x b x h / RULES.VOLUMETRIC_DIVISOR, to 2dp — a thin wrapper around
 * weightMath's own `volumetricKg`, which does the actual arithmetic (the
 * divisor, the multiplication and the round2 step live there, singly — this
 * file does not re-derive them).
 *
 * This wrapper adds the one thing Packing needs that the shared function
 * doesn't provide: a `null` for an incomplete OR non-positive triple (a
 * dimension of zero or less is bad data, not a legitimate "boxless"
 * material), so a caller can render "dimensions missing" rather than a
 * false zero. Compare catalogueMath.js's own `volumetricKg`, which returns
 * null only for a wholly absent `dims` and otherwise defers straight to the
 * shared implementation — a different, deliberately un-merged contract,
 * because Catalogue and Packing guard against different bad inputs.
 */
export function volumetric(dimensions) {
  if (!dimensions || dimensions.length !== 3) return null
  const [l, b, h] = dimensions
  if (!(l > 0) || !(b > 0) || !(h > 0)) return null
  return sharedVolumetricKg(dimensions)
}

/**
 * Which of the two weights the bill actually lands on: 'none' when there is
 * no volumetric figure to compare against, 'level' when the two agree
 * within the 0.005 kg tolerance, else whichever of dead/volumetric is
 * strictly the larger.
 */
export function governedBy(deadKg, volKg) {
  if (volKg == null) return 'none'
  if (withinTolerance(deadKg, volKg)) return 'level'
  return (Number(deadKg) || 0) > (Number(volKg) || 0) ? 'dead' : 'volumetric'
}

export function packById(packs, id) {
  return (packs || []).find((p) => p.id === id) || null
}

/** Human label for a packing-material id, by lookup in a `packs` list. 'Not set' is the mockup's own wording for an unmapped SKU. */
export function packLabel(packs, id) {
  const p = packById(packs, id)
  return p ? p.label : 'Not set'
}

/** How many SKUs and observed baskets a material carries. Drives every cascade warning before an edit or removal. */
export function packUsage(state, packId) {
  return {
    skus: (state.skus || []).filter((s) => s.packId === packId).length,
    combos: (state.combos || []).filter((c) => c.packId === packId).length,
  }
}

/** A fresh id for a newly minted material: the kind's stem plus the lowest free `-N<n>` suffix. */
export function mintPackId(packs, kind) {
  const stem = PACK_ID_STEM[kind] || PACK_ID_STEM[PACK_KIND.OTHER]
  const taken = new Set((packs || []).map((p) => p.id))
  let n = 1
  while (taken.has(`${stem}-N${n}`)) n += 1
  return `${stem}-N${n}`
}

/**
 * One row per category/sub-category pair, taken from the list AND from what
 * the SKUs actually carry, so a pair carried by SKUs but missing from the
 * list is visible (`listed: false`) rather than silently absent. `frozen`
 * and `pending` count only Sub-category-level requests — a SKU-level
 * request whose subject happens to read like a sub-category name must not
 * be counted here.
 */
/** Blank, whitespace and missing all mean the same thing: the merchant has not filed this yet. */
function named(v) {
  return typeof v === 'string' && v.trim() !== ''
}

export function subcategoryRows(state) {
  const seen = new Map()
  const add = (cat, sub, listed) => {
    const key = cat + ARROW + sub
    const existing = seen.get(key)
    if (existing) {
      if (listed) existing.listed = true
      return
    }
    seen.set(key, {key, cat, sub, listed, count: 0, ships: 0, frozen: 0, pending: 0})
  }

  const map = state.subcats || {}
  Object.keys(map).forEach((cat) => (map[cat] || []).forEach((sub) => add(cat, sub, true)))
  // A SKU with no category or no sub-category is not a taxonomy entry — it is a SKU waiting to be
  // filed. Admitting one here produced a row with both name cells blank, tagged "carried by SKUs,
  // not on the list" and offering to add a nameless sub-category to the list. Those SKUs are already
  // reported by the uncategorised counter above the table, which is where they belong.
  ;(state.skus || []).forEach((s) => { if (named(s.cat) && named(s.sub)) add(s.cat, s.sub, false) })
  ;(state.skus || []).forEach((s) => {
    const row = seen.get(s.cat + ARROW + s.sub)
    if (!row) return
    row.count += 1
    row.ships += s.ships || 0
  })
  ;(state.requests || []).forEach((q) => {
    if (q.level !== 'Sub-category') return
    seen.forEach((row) => {
      if (row.sub !== q.subject) return
      if (q.kind === 'approved') row.frozen += 1
      if (q.kind === 'review') row.pending += 1
    })
  })

  return Array.from(seen.values()).sort((a, b) => a.cat.localeCompare(b.cat) || a.sub.localeCompare(b.sub))
}

/** Order-independent identity for a basket, quantities included. */
export function basketKey(items) {
  return (items || []).map((i) => `${i.sku}×${i.qty || 1}`).sort().join(' + ')
}

/** The same basket ignoring quantities — the near-miss lookup. */
export function basketSetKey(items) {
  return (items || []).map((i) => i.sku).sort().join(' + ')
}

/**
 * Exact match first; a near miss (same SKUs, different quantities) only when
 * there is no exact one. Compares against `itemSkus` — the resolved
 * catalogue codes — never against the raw `items` labels, which live in a
 * different naming space (see the dataset header): most combo item labels
 * do not textually match a catalogue SKU label, which is exactly why
 * `itemSkus` resolves for only a fraction of combos.
 */
export function findBasket(combos, picked) {
  const exactKey = basketKey(picked)
  const setKey = basketSetKey(picked)
  const asItems = (c) => (c.itemSkus || []).map((sku, i) => ({sku, qty: c.items[i].qty || 1}))
  let exact = null
  let near = null
  ;(combos || []).forEach((c) => {
    if (!c.priceable) return
    if (!exact && basketKey(asItems(c)) === exactKey) exact = c
    if (!near && basketSetKey(asItems(c)) === setKey) near = c
  })
  return {exact, near: exact ? null : near}
}

/**
 * The baskets a computed weight can honestly be checked against: 2+ legs,
 * every leg resolvable (`priceable`), and every leg still present in THIS
 * session's catalogue (a sub-category removal never deletes SKUs, but a
 * later Catalogue merge could, so the membership check is not redundant).
 * Busiest first.
 */
export function observedBaskets(state) {
  const have = new Set((state.skus || []).map((s) => s.sku))
  return (state.combos || [])
    .filter((c) => c.priceable && (c.itemSkus || []).length >= 2 && c.itemSkus.every((code) => have.has(code)))
    .slice()
    .sort((a, b) => b.ships - a.ships)
}

/**
 * The arithmetic the calculator shows rather than asserts.
 *
 * Volumetric weight is a property of the box, so it does NOT sum across
 * legs — it is the material's own figure (read straight off `pack.volKg`,
 * which the store's cascade keeps in sync with `pack.dims` via this same
 * `weightMath.volumetricKg`) or nothing. Dead weight does sum: per line at
 * 3dp (so 0.75 x 3 is exactly 2.25, not 2.2499999999999996), the sum at
 * 2dp, then the material's own self-weight in kg at 3dp on top. `chargeKg`
 * goes through the shared `chargeable` (== weightMath's `chargeableKg`)
 * rather than a bare `Math.max` — it treats a `null` volKg as 0, so dead
 * weight alone survives untouched when the material has no dimensions.
 *
 * A picked SKU no longer in `skusByCode` is dropped, not crashed on — the
 * calculator must still render something sane if the catalogue changed
 * under it.
 */
export function basketWorking({picked, skusByCode, pack}) {
  const live = (picked || []).filter((p) => skusByCode[p.sku])
  const units = live.reduce((a, p) => a + (p.qty || 0), 0)

  const lines = live.map((p) => {
    const s = skusByCode[p.sku]
    return {sku: p.sku, qty: p.qty, sub: s.sub, dead: s.deadKg, total: round3((s.deadKg || 0) * p.qty)}
  })

  const skuDead = round2(lines.reduce((a, l) => a + l.total, 0))
  const packSelfKg = pack ? round3((pack.selfWeightG || 0) / 1000) : 0
  const deadTotal = round2(skuDead + packSelfKg)
  const volKg = pack && pack.volKg != null ? pack.volKg : null
  const chargeKg = chargeable(deadTotal, volKg)

  return {
    live, units,
    // One SKU at quantity two IS a basket — it is two units in one parcel.
    enough: live.length >= 1 && units >= 2,
    lines, skuDead, packSelfKg, deadTotal, volKg, chargeKg,
    governs: governedBy(deadTotal, volKg),
  }
}
